import { API_BASE_URL } from '@/config/api'
import { ApiErrorResponse } from '@/types/api'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type RequestOptions = RequestInit & { returnRawResponse?: boolean }

export class ApiError extends Error implements ApiErrorResponse {
  statusCode: number
  error: string

  constructor(message: string, statusCode: number, error?: string) {
    super(message)
    this.name = 'ApiError'

    this.error = error ?? 'ServerError'
    this.statusCode = statusCode
  }
}

export class ApiService {
  public readonly API_BASE_URL = API_BASE_URL

  private async request(
    path: string,
    method: HttpMethod,
    options: RequestOptions
  ) {
    const url = new URL(this.API_BASE_URL + path)
    const isMultiPartFormData = options.body instanceof FormData

    const res = await fetch(url.toString(), {
      ...options,
      method: method ?? 'GET',
      headers: {
        'x-mydra-app': 'aiBusiness',
        ...(isMultiPartFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options?.headers,
      },
      signal: options.signal,
      credentials: 'include',
    })

    if (!res.ok) {
      let json

      try {
        json = await res.json()
      } catch {
        json = {}
      }

      throw new ApiError(
        json.message ?? res.statusText,
        res.status,
        json.error ?? ''
      )
    }

    if (options.returnRawResponse) {
      return res
    }

    try {
      return await res.json()
    } catch {
      return undefined
    }
  }

  async get(path: string, options?: RequestOptions) {
    return await this.request(path, 'GET', { ...options })
  }

  async post(path: string, payload: unknown, options?: RequestOptions) {
    const isMultiPartFormData = payload instanceof FormData
    return await this.request(path, 'POST', {
      body: isMultiPartFormData ? payload : JSON.stringify(payload),
      ...options,
    })
  }

  async put(path: string, payload: unknown, options?: RequestOptions) {
    return await this.request(path, 'PUT', {
      body: JSON.stringify(payload),
      ...options,
    })
  }

  async patch(path: string, payload: unknown, options?: RequestOptions) {
    return await this.request(path, 'PATCH', {
      body: JSON.stringify(payload),
      ...options,
    })
  }

  async delete(path: string, options?: RequestOptions) {
    return await this.request(path, 'DELETE', { ...options })
  }
}
